import { Box, Chip, Grid, Link, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { formatDate } from 'src/sections/employer-data/shared/utils/policyUtils';

import type { AssignedRiskApplicationEntryData } from './assigned-risk-application-entry.types';

type AssignedRiskApplicationEntryProps = Readonly<{
  application: AssignedRiskApplicationEntryData;
  comboId: string;
  coverageId: string;
}>;

export default function AssignedRiskApplicationEntry({
  application,
  comboId,
  coverageId,
}: AssignedRiskApplicationEntryProps) {
  const assignedRiskAppId = application.assignedRiskAppId;
  const hasValidApplicationId = typeof assignedRiskAppId === 'number' && Number.isFinite(assignedRiskAppId);

  const content = (
    <Box
      sx={{
        mt: 3,
        p: 2.5,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        '&:hover': { backgroundColor: 'action.hover' },
      }}
      data-testid="assigned-risk-application-card"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" data-testid="assigned-risk-application-name">
          {application.applicationName ?? 'Assigned Risk application'}
        </Typography>
        <Chip
          label={application.appStatusDescription ?? 'Unknown'}
          color={application.appStatusDescription === 'Declined' ? 'error' : 'info'}
          variant="soft"
          size="small"
        />
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body2" color="text.secondary">Effective date</Typography>
          <Typography variant="subtitle2" data-testid="assigned-risk-effective-date">
            {formatDate(application.coverageEffectiveDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body2" color="text.secondary">Expiration date</Typography>
          <Typography variant="subtitle2" data-testid="assigned-risk-expiration-date">
            {formatDate(application.coverageExpirationDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="body2" color="text.secondary">Carrier name</Typography>
          <Typography variant="subtitle2" data-testid="assigned-risk-carrier-name">
            {application.carrierName ?? ''}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  if (!hasValidApplicationId) {
    return content;
  }

  const applicationPath = application.policyId != null
    ? paths.dashboard.employer_data.assigned_risk_application.details(
        comboId,
        coverageId,
        application.policyId,
        assignedRiskAppId,
      )
    : paths.dashboard.employer_data.assigned_risk_application.prePolicyDetails(
        comboId,
        coverageId,
        assignedRiskAppId,
      );

  return (
    <Link
      component={RouterLink as React.ElementType}
      to={applicationPath}
      color="inherit"
      underline="none"
      data-testid="assigned-risk-application-link"
    >
      {content}
    </Link>
  );
}
